import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import LiveCard from "@/components/feature/LiveCard";
import EmptyState from "@/components/base/EmptyState";
import PlayerDemo from "@/pages/live/components/PlayerDemo";
import DanmakuPanel from "@/pages/live/components/DanmakuPanel";
import { categories } from "@/mocks/categories";
import { getAnchorById } from "@/mocks/anchors";
import { getLiveById, getRelatedLives } from "@/utils/liveData";
import { formatStartTime, regionLabel } from "@/utils/format";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function LiveDetail() {
  const { liveId = "" } = useParams();
  const live = getLiveById(liveId);

  const anchor = live ? getAnchorById(live.anchorId) : undefined;
  const category = live ? categories.find((cat) => cat.key === live.categoryKey) : undefined;

  usePageMeta({
    title: live
      ? `${live.title} - ${anchor?.name ?? "主播"} - 蜜梨直播`
      : "直播详情 - 蜜梨直播",
    description: live
      ? `${live.description.slice(0, 60)}…主播${anchor?.name ?? ""}的${category?.name ?? ""}直播，尽在蜜梨直播。`
      : "蜜梨直播详情页",
    keywords: live ? `${live.title},${anchor?.name},蜜梨直播,在线直播` : "蜜梨直播详情",
  });

  if (!live) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="w-full flex-1 px-4 py-16 md:px-8">
          <EmptyState
            title="直播不存在或已结束"
            description="你访问的直播间可能已下线，返回首页看看其他精彩直播吧。"
            linkTo="/"
          />
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedLives(live, 4);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="w-full flex-1 px-4 py-6 md:px-8">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-base" />
          {category && (
            <>
              <Link
                to={`/category/${category.key}`}
                className="transition-colors hover:text-primary-600"
              >
                {category.name}
              </Link>
              <i className="ri-arrow-right-s-line text-base" />
            </>
          )}
          <span className="truncate font-medium text-foreground-950">{live.title}</span>
        </nav>

        <div className="mt-4 flex flex-col gap-6 lg:flex-row">
          <div className="min-w-0 flex-1">
            <PlayerDemo live={live} />

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  {category?.name}
                </span>
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-foreground-600">
                  {regionLabel(live.region)}
                </span>
                {live.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-xl font-extrabold leading-snug text-foreground-950 md:text-2xl">
                {live.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-base text-primary-500" />
                  {anchor?.name}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-base text-primary-500" />
                  开播于 {formatStartTime(live.startTime)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line text-base text-primary-500" />
                  弹幕 {live.danmaku}
                </span>
              </div>

              <div className="mt-5 border-t border-background-200 pt-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">直播简介</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {live.description}
                </p>

                <h2 className="mt-5 font-heading text-sm font-bold text-foreground-950">
                  互动规则说明
                </h2>
                <ul className="mt-2 space-y-2 text-sm leading-relaxed text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-500" />
                    文明发言，尊重主播与其他观众，禁止发布违规内容。
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-500" />
                    弹幕与点赞为前端演示交互，不会真实发送到弹幕服务器。
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-500" />
                    直播画面为演示效果，真实高清直播流待后端接口接入。
                  </li>
                </ul>
              </div>
            </section>

            <section className="mt-8">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-foreground-950">相关推荐</h2>
                <span className="text-xs text-foreground-400">根据当前分类与主播智能关联</span>
              </div>
              {related.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  {related.map((item) => (
                    <LiveCard key={item.id} live={item} size="compact" />
                  ))}
                </div>
              ) : (
                <EmptyState title="暂无相关推荐" description="稍后再来看看吧。" linkTo="/" />
              )}
            </section>
          </div>

          <aside className="w-full shrink-0 lg:w-80">
            <div className="lg:sticky lg:top-20">
              <DanmakuPanel liveTitle={live.title} />

              <div className="mt-4 flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-4">
                <Link
                  to={category ? `/category/${category.key}` : "/"}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-100 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-secondary-200"
                >
                  <i className="ri-apps-2-line text-base" />
                  返回分类：{category?.name}
                </Link>
                <Link
                  to="/"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-home-4-line text-base" />
                  返回首页
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}